import {useState,useCallback,useRef} from 'react'

const MAX_HISTORY=50

/**
 * useUndoRedo — command-pattern history for node/edge state.
 * Call pushSnapshot() AFTER every discrete user action.
 * undo() / redo() return the {nodes,edges} to restore (or null).
 */
export default function useUndoRedo() {
    const stackRef=useRef([])     // [{nodes,edges}]
    const indexRef=useRef(-1)
    const [canUndo,setCanUndo]=useState(false)
    const [canRedo,setCanRedo]=useState(false)

    const syncFlags=() => {
        setCanUndo(indexRef.current>0)
        setCanRedo(indexRef.current<stackRef.current.length-1)
    }

    const pushSnapshot=useCallback((nodes,edges) => {
        // Discard any redo history ahead of current position
        stackRef.current=stackRef.current.slice(0,indexRef.current+1)

        stackRef.current.push({
            nodes: nodes.map(n => ({...n,data: {...n.data}})),
            edges: edges.map(e => ({...e}))
        })

        // Keep at most MAX_HISTORY entries
        if(stackRef.current.length>MAX_HISTORY) {
            stackRef.current=stackRef.current.slice(stackRef.current.length-MAX_HISTORY)
        }

        indexRef.current=stackRef.current.length-1
        syncFlags()
    },[])

    const undo=useCallback(() => {
        if(indexRef.current<=0) return null
        indexRef.current--
        const state=stackRef.current[indexRef.current]
        syncFlags()
        return state
    },[])

    const redo=useCallback(() => {
        if(indexRef.current>=stackRef.current.length-1) return null
        indexRef.current++
        const state=stackRef.current[indexRef.current]
        syncFlags()
        return state
    },[])

    /** Replace the most-recent snapshot (use when finishing a drag, not on every mousemove) */
    const replaceTopSnapshot=useCallback((nodes,edges) => {
        if(stackRef.current.length===0||indexRef.current<0) {
            pushSnapshot(nodes,edges)
            return
        }
        stackRef.current[indexRef.current]={
            nodes: nodes.map(n => ({...n,data: {...n.data}})),
            edges: edges.map(e => ({...e}))
        }
    },[pushSnapshot])

    return {pushSnapshot,replaceTopSnapshot,undo,redo,canUndo,canRedo}
}
